import React, { useEffect, useMemo, useState } from "react";
import { Page, PageHeader } from "../components/PageHeader";
import { api, extractError } from "../lib/api";
import { fmtCLP, fmtDate, todayISO } from "../lib/format";
import { toast } from "sonner";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

const monthLabel = (yyyymm) => {
  const [y, m] = yyyymm.split("-");
  const meses = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
  return `${meses[parseInt(m, 10) - 1]} ${y}`;
};

export default function FlujoCaja() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Default: current year YTD
  const [desde, setDesde] = useState(`${new Date().getFullYear()}-01-01`);
  const [hasta, setHasta] = useState(todayISO());

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (desde) params.desde = desde;
      if (hasta) params.hasta = hasta;
      const { data } = await api.get("/reportes/flujo-caja", { params });
      setData(data);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [desde, hasta]);

  // Aggregate by month: entradas, salidas, neto, saldo running
  const monthly = useMemo(() => {
    if (!data) return [];
    const byMonth = {};
    for (const e of data.entradas) {
      const ym = (e.fecha || "").slice(0, 7);
      if (!ym) continue;
      byMonth[ym] = byMonth[ym] || { ym, entradas: 0, salidas: 0 };
      byMonth[ym].entradas += e.monto || 0;
    }
    for (const s of data.salidas) {
      const ym = (s.fecha || "").slice(0, 7);
      if (!ym) continue;
      byMonth[ym] = byMonth[ym] || { ym, entradas: 0, salidas: 0 };
      byMonth[ym].salidas += s.monto || 0;
    }
    const arr = Object.values(byMonth).sort((a, b) => a.ym.localeCompare(b.ym));
    let saldo = data.saldo_inicial || 0;
    return arr.map((m) => {
      const neto = m.entradas - m.salidas;
      saldo += neto;
      return {
        ym: m.ym,
        label: monthLabel(m.ym),
        entradas: Math.round(m.entradas),
        salidas: Math.round(m.salidas),
        neto: Math.round(neto),
        saldo: Math.round(saldo),
      };
    });
  }, [data]);

  // Combined chronological movements for detail view
  const movimientos = useMemo(() => {
    if (!data) return [];
    const arr = [
      ...data.entradas.map((e) => ({ ...e, kind: "entrada" })),
      ...data.salidas.map((s) => ({ ...s, kind: "salida" })),
    ];
    return arr.sort((a, b) => (a.fecha || "").localeCompare(b.fecha || ""));
  }, [data]);

  return (
    <Page>
      <PageHeader
        title="Flujo de caja"
        subtitle="Movimientos de caja y banco con saldo running"
        testid="fc-header"
        actions={
          <div className="flex items-center gap-2">
            <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} data-testid="fc-desde"
              className="px-2 py-1.5 border border-border rounded-sm text-sm" />
            <span className="text-xs text-muted-foreground">→</span>
            <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} data-testid="fc-hasta"
              className="px-2 py-1.5 border border-border rounded-sm text-sm" />
          </div>
        }
      />

      {data && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
          <KPI label="Saldo inicial" value={fmtCLP(data.saldo_inicial)} />
          <KPI label="Entradas" value={fmtCLP(data.total_entradas)} tone="primary" />
          <KPI label="Salidas" value={fmtCLP(data.total_salidas)} tone="danger" />
          <KPI label="Flujo neto" value={fmtCLP(data.flujo_neto, { showSign: true })} tone={data.flujo_neto >= 0 ? "primary" : "danger"} />
          <KPI label="Saldo final" value={fmtCLP(data.saldo_final)} tone="primary" />
        </div>
      )}

      <Tabs defaultValue="mensual" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="mensual" data-testid="fc-tab-mensual">Resumen mensual</TabsTrigger>
          <TabsTrigger value="grafico" data-testid="fc-tab-grafico">Gráfico</TabsTrigger>
          <TabsTrigger value="movimientos" data-testid="fc-tab-mov">Movimientos</TabsTrigger>
        </TabsList>

        <TabsContent value="mensual">
          <div className="bg-card border border-border rounded-md overflow-auto">
            {loading ? (
              <div className="p-8 text-center text-muted-foreground text-sm">Cargando…</div>
            ) : monthly.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground text-sm">
                Sin movimientos de caja/banco en el período. Verifica tus asientos en Libro Diario.
              </div>
            ) : (
              <table className="acc-table" data-testid="fc-monthly-table">
                <thead>
                  <tr>
                    <th>Mes</th>
                    <th className="num">Entradas</th>
                    <th className="num">Salidas</th>
                    <th className="num">Flujo neto</th>
                    <th className="num">Saldo acumulado</th>
                  </tr>
                </thead>
                <tbody>
                  {monthly.map((m) => (
                    <tr key={m.ym}>
                      <td className="font-medium">{m.label}</td>
                      <td className="num text-primary">{fmtCLP(m.entradas)}</td>
                      <td className="num text-destructive">{fmtCLP(m.salidas)}</td>
                      <td className={`num font-semibold ${m.neto >= 0 ? "text-primary" : "text-destructive"}`}>
                        {fmtCLP(m.neto, { showSign: true })}
                      </td>
                      <td className="num font-semibold">{fmtCLP(m.saldo)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-muted font-semibold">
                    <td className="text-right pr-3 label-caps">Totales</td>
                    <td className="num text-primary">{fmtCLP(data?.total_entradas || 0)}</td>
                    <td className="num text-destructive">{fmtCLP(data?.total_salidas || 0)}</td>
                    <td className="num">{fmtCLP(data?.flujo_neto || 0, { showSign: true })}</td>
                    <td className="num">{fmtCLP(data?.saldo_final || 0)}</td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="grafico">
          {monthly.length === 0 ? (
            <div className="bg-card border border-border rounded-md p-8 text-center text-muted-foreground text-sm">
              Sin datos para graficar.
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="fc-charts">
              <div className="bg-card border border-border rounded-md p-4">
                <div className="label-caps mb-3">Saldo acumulado</div>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={monthly}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2DFD8" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => fmtCLP(v)} />
                    <Line type="monotone" dataKey="saldo" stroke="#1B4332" strokeWidth={2} dot={{ r: 3 }} name="Saldo" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="bg-card border border-border rounded-md p-4">
                <div className="label-caps mb-3">Entradas vs Salidas</div>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={monthly}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2DFD8" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => fmtCLP(v)} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="entradas" fill="#2D6A4F" name="Entradas" />
                    <Bar dataKey="salidas" fill="#A94438" name="Salidas" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="movimientos">
          <div className="bg-card border border-border rounded-md overflow-auto">
            {movimientos.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground text-sm">Sin movimientos.</div>
            ) : (
              <table className="acc-table" data-testid="fc-mov-table">
                <thead>
                  <tr>
                    <th style={{ width: 90 }}>Fecha</th>
                    <th style={{ width: 70 }}>Asiento</th>
                    <th>Glosa</th>
                    <th>Cuenta</th>
                    <th className="num">Entrada</th>
                    <th className="num">Salida</th>
                  </tr>
                </thead>
                <tbody>
                  {movimientos.map((m, i) => (
                    <tr key={i}>
                      <td>{fmtDate(m.fecha)}</td>
                      <td className="font-mono text-[12px]">#{m.numero}</td>
                      <td>{m.glosa}</td>
                      <td className="text-[12px] text-muted-foreground">{m.cuenta}</td>
                      <td className="num text-primary">{m.kind === "entrada" ? fmtCLP(m.monto) : ""}</td>
                      <td className="num text-destructive">{m.kind === "salida" ? fmtCLP(m.monto) : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </Page>
  );
}

function KPI({ label, value, tone = "default" }) {
  const tones = {
    default: "text-foreground",
    primary: "text-primary",
    danger: "text-destructive",
    warning: "text-warning-foreground",
  };
  return (
    <div className="bg-card border border-border rounded-md p-4">
      <div className="label-caps mb-1.5">{label}</div>
      <div className={`num text-lg font-semibold ${tones[tone]}`}>{value}</div>
    </div>
  );
}
